import React, { useEffect } from 'react';
import { Modal, ModalHeader, ModalBody, Table, Badge, Spinner, Alert, Card, CardBody, Row, Col } from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';
import { getProcessLogsByRequestId } from '../../../slices/projects/processLogs/thunk';
import { clearLogs } from '../../../slices/projects/processLogs/reducer';

interface ProcessLogsModalProps {
  isOpen: boolean;
  toggle: () => void;
  requestId: number | null;
}

const ProcessLogsModal: React.FC<ProcessLogsModalProps> = ({ isOpen, toggle, requestId }) => {
  const dispatch = useDispatch();
  const { logs, loading, error } = useSelector((state: any) => state.ProcessLogs);

  useEffect(() => {
    if (isOpen && requestId) {
      dispatch(getProcessLogsByRequestId(requestId) as any);
    }

    // Clear logs when modal closes
    return () => {
      if (!isOpen) {
        dispatch(clearLogs());
      }
    };
  }, [isOpen, requestId, dispatch]);

  const getStepLabel = (step: string): string => {
    const labels: Record<string, string> = {
      'REQUEST_INITIATED': 'Request Initiated',
      'QUOTA_CHECK': 'Quota Check',
      'AI_SERVICE_CALL': 'AI Service Call',
      'PROCESSOR_FOUND': 'Processor Found',
      'DOCUMENT_PROCESSING': 'Document Processing',
      'TEXT_EXTRACTION': 'Text Extraction',
      'CLAUDE_START': 'Claude Started',
      'CLAUDE_COMPLETE': 'Claude Complete',
      'PROCESSING_COMPLETE': 'Processing Complete',
      'ERROR': 'Error'
    };
    return labels[step] || step;
  };

  const getStatusBadge = (status: number) => {
    switch (status) {
      case 1: // SUCCESS
        return <Badge color="success">Success</Badge>;
      case 2: // FAILED
        return <Badge color="danger">Failed</Badge>;
      case 3: // WARNING
        return <Badge color="warning">Warning</Badge>;
      default:
        return <Badge color="secondary">Unknown</Badge>;
    }
  };

  const getStatusCounts = () => {
    const counts = { success: 0, failed: 0, warning: 0 };
    logs.forEach((log: any) => {
      if (log.status === 1) counts.success++;
      else if (log.status === 2) counts.failed++;
      else if (log.status === 3) counts.warning++;
    });
    return counts;
  };

  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleString();
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="xl" scrollable>
      <ModalHeader toggle={toggle}>
        Process Logs {requestId && `- Request #${requestId}`}
      </ModalHeader>
      <ModalBody>
        {loading && (
          <div className="text-center py-4">
            <Spinner color="primary" />
            <p className="mt-2">Loading process logs...</p>
          </div>
        )}

        {error && (
          <Alert color="danger">
            <strong>Error: </strong>{error}
          </Alert>
        )}

        {!loading && !error && logs.length === 0 && (
          <Alert color="info">
            No process logs found for this request.
          </Alert>
        )}

        {!loading && !error && logs.length > 0 && (
          <>
            {/* Summary Section */}
            <Card className="mb-3">
              <CardBody>
                <h6 className="mb-3">Summary</h6>
                <Row>
                  <Col xs="4">
                    <div className="text-center">
                      <Badge color="success" className="fs-5 px-3 py-2">
                        {getStatusCounts().success}
                      </Badge>
                      <p className="mt-2 mb-0 text-muted">Success</p>
                    </div>
                  </Col>
                  <Col xs="4">
                    <div className="text-center">
                      <Badge color="danger" className="fs-5 px-3 py-2">
                        {getStatusCounts().failed}
                      </Badge>
                      <p className="mt-2 mb-0 text-muted">Failed</p>
                    </div>
                  </Col>
                  <Col xs="4">
                    <div className="text-center">
                      <Badge color="warning" className="fs-5 px-3 py-2">
                        {getStatusCounts().warning}
                      </Badge>
                      <p className="mt-2 mb-0 text-muted">Warning</p>
                    </div>
                  </Col>
                </Row>
              </CardBody>
            </Card>

            {/* Logs Table */}
            <div className="table-responsive">
              <Table hover className="align-middle table-nowrap mb-0">
                <thead className="table-light">
                  <tr>
                    <th style={{ width: '60px' }}>Step #</th>
                    <th style={{ width: '180px' }}>Step Code</th>
                    <th>Subject</th>
                    <th>Details</th>
                    <th style={{ width: '100px' }}>Status</th>
                    <th style={{ width: '180px' }}>Timestamp</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log: any, index: number) => (
                    <tr key={log.logId}>
                      <td className="fw-medium">{index + 1}</td>
                      <td>
                        <Badge color="secondary" className="font-monospace">
                          {log.step}
                        </Badge>
                      </td>
                      <td>{log.subject || '-'}</td>
                      <td>
                        <small className="text-muted">{log.logDetail || '-'}</small>
                      </td>
                      <td>{getStatusBadge(log.status)}</td>
                      <td>
                        <small>{formatTimestamp(log.createdAt)}</small>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </>
        )}
      </ModalBody>
    </Modal>
  );
};

export default ProcessLogsModal;
